import { subYears } from 'date-fns'
import { Timestamp } from 'firebase/firestore'

import type { FilingStatusHousehold } from '#dictionary/filingStatuses'
import type { StandardDeductionProfile } from '#dictionary/standardDeductions/getStandardDeductionGroup'

import MemberService from './MemberService'
import type { MaritalStatus, ProfileDoc } from './schemas'

// #DK: Currently we only ask the user for the number of minor children and other dependents.
// The calculator already supports real member records and relies on them,
// because the set of dependents can change from year to year.
// For now, we generate mock member objects based solely on these counts,
// but in the future this will be replaced with real dependent member data from the backend.
const createMockedMember = (
  index: number,
  kind: 'child' | 'dependent'
): MemberService => {
  const label = kind.charAt(0).toUpperCase() + kind.slice(1)
  const name = `${label} ${index + 1}`
  const age = kind === 'child' ? 10 : 50
  const birthday = Timestamp.fromDate(subYears(new Date(), age))
  const retirementAge = 75
  const zipCode = '10001'

  return new MemberService(
    { name, birthday, retirementAge, socialSecurityAge: null, zipCode },
    `${kind}-${index + 1}`,
    kind
  )
}

const mockDependents = (
  length: number,
  kind: 'child' | 'dependent'
): MemberService[] => {
  return Array.from({ length }, (_, i) => createMockedMember(i, kind))
}

// TODO: #should have not optional fields from the backend
// We have to use codecs to get the default values
// Reference: apps/src/features/profile/schemas.ts
class ProfileService
  implements StandardDeductionProfile, FilingStatusHousehold
{
  public readonly maritalStatus: MaritalStatus
  public readonly filingJointly: boolean
  public readonly projectionStartDate: Date
  public readonly person: MemberService
  public readonly partner: MemberService | null
  public readonly minorChildren: MemberService[]
  public readonly otherDependents: MemberService[]

  constructor({ entity, createdAt }: ProfileDoc) {
    this.maritalStatus = entity.maritalStatus
    this.filingJointly = entity.filingJointly ?? true
    this.projectionStartDate = createdAt.toDate()

    const { person, partner } = entity.members
    this.person = new MemberService(person, 'person', 'person')
    this.partner = partner && new MemberService(partner, 'partner', 'partner')
    this.minorChildren = mockDependents(entity.minorChildrenCount ?? 0, 'child')
    this.otherDependents = mockDependents(
      entity.otherDependentsCount ?? 0,
      'dependent'
    )
  }

  get allMembers(): MemberService[] {
    return [
      this.person,
      this.partner,
      ...this.minorChildren,
      ...this.otherDependents,
    ].filter((member): member is MemberService => member !== null)
  }

  get projectionStartYear(): number {
    return this.projectionStartDate.getFullYear()
  }

  get projectionEndYear(): number {
    return this.person.getYearForAge(MemberService.lifeExpectancy)
  }

  childrenQualifiedForTaxCreditCount(year: number): number {
    return this.minorChildren.filter(child =>
      child.isQualifiedForChildTaxCredit(year)
    ).length
  }
}

export default ProfileService
